import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { demoAPI } from '../api';
import TranslateFromEnButton from '../components/TranslateFromEnButton';

/**
 * AdminOnboardingFlow — admin section to manage the demo spotlight tour.
 * Lives at Admin Panel → Onboarding tab.
 *
 * Features:
 *   - List of all steps with reorder ↑↓
 *   - Inline editor (title, body, helper, target preset, position, context, screenshot, enabled)
 *   - Add new / Delete
 *   - "Preview onboarding" → opens the demo board with ?preview_onboarding=1
 */

const POSITIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
];

const TARGET_MODES = [
  { value: 'preset', label: 'Curated preset' },
  { value: 'custom', label: 'Manual selector' },
];

const MATCH_MODES = [
  { value: '', label: 'Auto (follow preset)' },
  { value: 'first', label: 'First match only' },
  { value: 'group', label: 'Whole group (union)' },
];

const CONTEXTS = [
  { value: 'demo', label: 'Demo board only' },
  { value: 'library', label: 'Image library only' },
  { value: 'global', label: 'Globally (every board)' },
  { value: 'first_login', label: 'First login only' },
];

const labelStyle = {
  display: 'block',
  fontFamily: 'Playfair Display, serif',
  fontSize: 10,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: 'var(--kahla-text-light)',
  marginBottom: 6,
  fontWeight: 500,
};

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  border: '1px solid var(--kahla-border)',
  background: '#fff',
  fontFamily: 'inherit',
  fontSize: 14,
  color: 'var(--kahla-charcoal)',
  outline: 'none',
};

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'pl', label: 'Polski' },
  { code: 'de', label: 'Deutsch' },
];

const AdminOnboardingFlow = () => {
  const navigate = useNavigate();
  const [steps, setSteps] = useState([]);
  const [presets, setPresets] = useState([]);
  const [demoBoardId, setDemoBoardId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [openId, setOpenId] = useState(null); // currently expanded step
  const [activeLang, setActiveLang] = useState('en');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [stepsRes, presetsRes, tplRes] = await Promise.all([
        demoAPI.adminListSteps(),
        demoAPI.adminGetTargetPresets(),
        demoAPI.adminGetTemplate(),
      ]);
      setSteps(stepsRes.data.steps || []);
      setPresets(presetsRes.data.presets || []);
      setDemoBoardId(tplRes.data.template?.id || null);
    } catch (e) {
      // non-fatal
    } finally {
      setLoading(false);
    }
  };

  const showToast = (text, kind = 'success') => {
    setToast({ text, kind });
    setTimeout(() => setToast(null), 2500);
  };

  const handleField = (id, field, value) => {
    setSteps((prev) => prev.map((s) => s.id === id ? { ...s, [field]: value } : s));
  };

  // Read a localized value for a step in the currently-active editor language.
  // For 'en' we read the base step.title/body/helper. For 'pl'/'de' we read
  // step.translations[lang]?.title etc., falling back to ''.
  const readLocalized = (step, key) => {
    if (!step) return '';
    if (activeLang === 'en') return step[key] || '';
    const t = step.translations || {};
    return ((t[activeLang] || {})[key]) || '';
  };

  // Write a localized value back. Mirror of readLocalized — base for EN,
  // translations[lang] otherwise.
  const writeLocalized = (id, key, value) => {
    setSteps((prev) => prev.map((s) => {
      if (s.id !== id) return s;
      if (activeLang === 'en') return { ...s, [key]: value };
      const t = { ...(s.translations || {}) };
      t[activeLang] = { ...(t[activeLang] || {}), [key]: value };
      return { ...s, translations: t };
    }));
  };

  const handleSave = async (id) => {
    const step = steps.find((s) => s.id === id);
    if (!step) return;
    setSavingId(id);
    try {
      const { id: _, selector: __, created_at: ___, updated_at: ____, ...payload } = step;
      const res = await demoAPI.adminUpdateStep(id, payload);
      setSteps((prev) => prev.map((s) => s.id === id ? res.data : s));
      showToast('Step saved.');
    } catch (e) {
      showToast('Could not save step.', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this onboarding step?')) return;
    try {
      await demoAPI.adminDeleteStep(id);
      setSteps((prev) => prev.filter((s) => s.id !== id));
      showToast('Step deleted.');
    } catch (e) {
      showToast('Could not delete step.', 'error');
    }
  };

  const handleCreate = async () => {
    try {
      const res = await demoAPI.adminCreateStep({
        title: 'New step',
        body: '',
        helper: '',
        target: 'none',
        position: 'auto',
        context: 'demo',
        enabled: true,
      });
      setSteps((prev) => [...prev, res.data]);
      setOpenId(res.data.id);
      showToast('Step created.');
    } catch (e) {
      showToast('Could not create step.', 'error');
    }
  };

  const handleMove = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    [next[idx], next[target]] = [next[target], next[idx]];
    setSteps(next);
    try {
      await demoAPI.adminReorderSteps(next.map((s) => s.id));
    } catch (e) {
      showToast('Could not reorder steps.', 'error');
    }
  };

  const handleToggleEnabled = async (id, value) => {
    handleField(id, 'enabled', value);
    try {
      await demoAPI.adminUpdateStep(id, { enabled: value });
    } catch (e) {
      showToast('Could not update step.', 'error');
    }
  };

  // Open a live preview of the spotlight tour.
  //   ctx       — 'demo' | 'library'
  //   stepId    — optional, jumps the tour directly to that step
  //   step      — full step object (used for context auto-detect when stepId given)
  const handlePreview = (ctx = 'demo', stepId = null, step = null) => {
    const targetCtx = step?.context || ctx;
    const qs = stepId ? `&step=${encodeURIComponent(stepId)}` : '';
    if (targetCtx === 'library') {
      window.open(`/library?preview_onboarding=1${qs}`, '_blank');
      return;
    }
    if (!demoBoardId) {
      showToast('Set a demo template board first (Demo tab).', 'error');
      return;
    }
    window.open(`/editor/${demoBoardId}?preview_onboarding=1${qs}`, '_blank');
  };

  if (loading) {
    return <div style={{ padding: 40 }}><div className="spinner" /></div>;
  }

  return (
    <div data-testid="admin-onboarding-flow" style={{ display: 'flex', flexDirection: 'column', gap: 32, maxWidth: 980 }}>
      {toast && (
        <div data-testid="onboarding-toast" style={{
          position: 'fixed', top: 80, right: 28, zIndex: 9999,
          background: toast.kind === 'error' ? '#7a3a3a' : 'var(--kahla-charcoal)',
          color: '#fff', padding: '11px 22px',
          fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>{toast.text}</div>
      )}

      {/* Header */}
      <header style={{ display: 'flex', justifyContent: 'space-between', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <p style={{
            fontFamily: 'Playfair Display, serif', fontSize: 11,
            letterSpacing: '0.32em', textTransform: 'uppercase',
            color: 'var(--kahla-text-light)', margin: '0 0 10px', fontWeight: 300,
          }}>Onboarding flow</p>
          <h2 style={{
            fontFamily: 'Playfair Display, serif', fontSize: 28, fontWeight: 400,
            color: 'var(--kahla-charcoal)', margin: '0 0 8px', lineHeight: 1.2,
          }}>
            A guided creative introduction.
          </h2>
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
            fontSize: 16, color: 'var(--kahla-text-light)', margin: 0, lineHeight: 1.65, maxWidth: 640,
          }}>
            Compose the spotlight tour new users see on the demo board. Reorder, refine the tone, attach to real interface elements.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button
            type="button"
            data-testid="onboarding-preview-btn"
            onClick={() => handlePreview('demo')}
            disabled={!demoBoardId}
            title={demoBoardId ? '' : 'Set a demo template board first (Demo tab).'}
            style={{
              background: 'none', border: '1px solid var(--kahla-border)',
              padding: '11px 22px', cursor: demoBoardId ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: 'var(--kahla-charcoal)', opacity: demoBoardId ? 1 : 0.5,
            }}
          >
            Preview demo board
          </button>
          <button
            type="button"
            data-testid="onboarding-preview-library-btn"
            onClick={() => handlePreview('library')}
            style={{
              background: 'none', border: '1px solid var(--kahla-border)',
              padding: '11px 22px', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: 'var(--kahla-charcoal)',
            }}
          >
            Preview library
          </button>
          <button
            type="button"
            data-testid="onboarding-add-step-btn"
            onClick={handleCreate}
            style={{
              background: 'var(--kahla-charcoal)', color: '#fff', border: 'none',
              padding: '11px 22px',
              fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >+ Add step</button>
        </div>
      </header>

      {/* Steps list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {steps.length === 0 && (
          <p style={{
            fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
            color: 'var(--kahla-text-light)', textAlign: 'center', padding: 40,
          }}>No onboarding steps yet. Add the first one above.</p>
        )}
        {steps.map((step, idx) => {
          const isOpen = openId === step.id;
          return (
            <div
              key={step.id}
              data-testid={`onboarding-step-${step.id}`}
              style={{
                border: '1px solid var(--kahla-border)',
                background: '#fff',
                opacity: step.enabled ? 1 : 0.55,
              }}
            >
              {/* Row header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '14px 18px', cursor: 'pointer',
              }} onClick={() => setOpenId(isOpen ? null : step.id)}>
                <span style={{
                  fontFamily: 'Playfair Display, serif', fontSize: 11,
                  letterSpacing: '0.18em', color: 'var(--kahla-text-light)',
                  width: 28,
                }}>
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    fontFamily: 'Playfair Display, serif', fontSize: 15,
                    color: 'var(--kahla-charcoal)', margin: 0, lineHeight: 1.3,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{step.title || '(untitled)'}</p>
                  <p style={{
                    fontSize: 11, color: 'var(--kahla-text-light)', margin: '3px 0 0',
                    letterSpacing: '0.04em',
                  }}>
                    Target: <strong>{step.target}</strong> · {step.context} · {step.position}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleMove(idx, -1); }}
                  disabled={idx === 0}
                  data-testid={`step-move-up-${step.id}`}
                  style={iconBtn(idx === 0)}
                  title="Move up"
                >↑</button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleMove(idx, +1); }}
                  disabled={idx === steps.length - 1}
                  data-testid={`step-move-down-${step.id}`}
                  style={iconBtn(idx === steps.length - 1)}
                  title="Move down"
                >↓</button>
                <label
                  onClick={(e) => e.stopPropagation()}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--kahla-text-light)', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={!!step.enabled}
                    onChange={(e) => handleToggleEnabled(step.id, e.target.checked)}
                    data-testid={`step-enabled-${step.id}`}
                  />
                  Enabled
                </label>
                <span style={{ fontSize: 12, color: 'var(--kahla-text-light)' }}>{isOpen ? '▾' : '▸'}</span>
              </div>

              {/* Editor body */}
              {isOpen && (
                <div style={{ padding: '4px 22px 22px', borderTop: '1px solid var(--kahla-cream)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* Language tabs — apply to title / body / helper only.
                      Selector / position / context / screenshot stay shared across languages. */}
                  <div style={{ gridColumn: '1 / -1', borderBottom: '1px solid var(--kahla-border)', marginBottom: 4 }}>
                    <div style={{ display: 'flex', gap: 0 }}>
                      {LANGS.map((l) => (
                        <button
                          key={l.code}
                          type="button"
                          data-testid={`step-lang-${step.id}-${l.code}`}
                          onClick={() => setActiveLang(l.code)}
                          style={{
                            background: 'none', border: 'none', padding: '8px 16px',
                            fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            borderBottom: activeLang === l.code ? '2px solid var(--kahla-charcoal)' : '2px solid transparent',
                            color: activeLang === l.code ? 'var(--kahla-charcoal)' : 'var(--kahla-text-light)',
                            cursor: 'pointer', marginBottom: -1,
                          }}
                        >
                          {l.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* AI assist — appears only on PL/DE tabs. Re-uses the same
                      Claude flow as the Branding screen. */}
                  {activeLang !== 'en' && (
                    <div style={{ gridColumn: '1 / -1', marginTop: -4 }}>
                      <TranslateFromEnButton
                        targetLang={activeLang}
                        testId={`onboarding-translate-${step.id}-${activeLang}`}
                        getSourceTexts={() => ({
                          title: step.title || '',
                          body: step.body || '',
                          helper: step.helper || '',
                        })}
                        getTargetTexts={() => ({
                          title: ((step.translations || {})[activeLang] || {}).title || '',
                          body: ((step.translations || {})[activeLang] || {}).body || '',
                          helper: ((step.translations || {})[activeLang] || {}).helper || '',
                        })}
                        onTranslated={(translations) => {
                          setSteps((prev) => prev.map((s) => {
                            if (s.id !== step.id) return s;
                            const t = { ...(s.translations || {}) };
                            t[activeLang] = { ...(t[activeLang] || {}), ...translations };
                            return { ...s, translations: t };
                          }));
                        }}
                      />
                    </div>
                  )}

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>
                      Step title{activeLang !== 'en' ? ` · ${activeLang.toUpperCase()}` : ''}
                    </label>
                    <input
                      type="text"
                      value={readLocalized(step, 'title')}
                      onChange={(e) => writeLocalized(step.id, 'title', e.target.value)}
                      placeholder={activeLang !== 'en' ? (step.title || '') : ''}
                      data-testid={`step-title-${step.id}`}
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>
                      Step description{activeLang !== 'en' ? ` · ${activeLang.toUpperCase()}` : ''}
                    </label>
                    <textarea
                      value={readLocalized(step, 'body')}
                      onChange={(e) => writeLocalized(step.id, 'body', e.target.value)}
                      placeholder={activeLang !== 'en' ? (step.body || '') : ''}
                      data-testid={`step-body-${step.id}`}
                      rows={3}
                      style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>
                      Optional helper text{activeLang !== 'en' ? ` · ${activeLang.toUpperCase()}` : ''}
                    </label>
                    <input
                      type="text"
                      value={readLocalized(step, 'helper')}
                      onChange={(e) => writeLocalized(step.id, 'helper', e.target.value)}
                      placeholder={activeLang !== 'en' ? (step.helper || '') : ''}
                      data-testid={`step-helper-${step.id}`}
                      style={inputStyle}
                    />
                  </div>

                  {/* ============================================================
                       Spotlight — admin's full visual control surface.
                       ============================================================
                       Target mode (preset vs manual selector), grouped match
                       choice, soft "prefer grouped areas" nudge, and a
                       collapsible Fine-tune card for padding / width / offset.
                  */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <SpotlightEditor
                      step={step}
                      presets={presets}
                      onChange={(patch) => setSteps((prev) => prev.map((s) => s.id === step.id ? { ...s, ...patch } : s))}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>Position</label>
                    <select
                      value={step.position || 'auto'}
                      onChange={(e) => handleField(step.id, 'position', e.target.value)}
                      data-testid={`step-position-${step.id}`}
                      style={inputStyle}
                    >
                      {POSITIONS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Visible in</label>
                    <select
                      value={step.context || 'demo'}
                      onChange={(e) => handleField(step.id, 'context', e.target.value)}
                      data-testid={`step-context-${step.id}`}
                      style={inputStyle}
                    >
                      {CONTEXTS.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Optional screenshot URL</label>
                    <input
                      type="text"
                      value={step.screenshot_url || ''}
                      onChange={(e) => handleField(step.id, 'screenshot_url', e.target.value)}
                      data-testid={`step-screenshot-${step.id}`}
                      placeholder="https://…"
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, gap: 12, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => handleDelete(step.id)}
                      data-testid={`step-delete-${step.id}`}
                      style={{
                        background: 'none', border: 'none', padding: '4px 0',
                        fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.14em',
                        textTransform: 'uppercase', color: '#7a3a3a',
                        textDecoration: 'underline', textUnderlineOffset: 4,
                        cursor: 'pointer',
                      }}
                    >Delete step</button>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <button
                        type="button"
                        onClick={() => handlePreview(step.context || 'demo', step.id, step)}
                        data-testid={`step-preview-${step.id}`}
                        title="Open the live spotlight on the demo board with this step active"
                        style={{
                          background: 'none', border: '1px solid var(--kahla-border)',
                          padding: '10px 18px',
                          fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em',
                          textTransform: 'uppercase', color: 'var(--kahla-charcoal)',
                          cursor: 'pointer',
                        }}
                      >Preview step</button>
                      <button
                        type="button"
                        onClick={() => handleSave(step.id)}
                        disabled={savingId === step.id}
                        data-testid={`step-save-${step.id}`}
                        style={{
                          background: 'var(--kahla-charcoal)', color: '#fff', border: 'none',
                          padding: '11px 22px',
                          fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em',
                          textTransform: 'uppercase',
                          cursor: savingId === step.id ? 'wait' : 'pointer',
                          opacity: savingId === step.id ? 0.6 : 1,
                        }}
                      >
                        {savingId === step.id ? 'Saving' : 'Save step'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const iconBtn = (disabled) => ({
  background: 'none',
  border: '1px solid var(--kahla-border)',
  width: 28, height: 26,
  fontSize: 12,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  color: 'var(--kahla-charcoal)',
  fontFamily: 'inherit',
});

// ============================================================
// SpotlightEditor — admin's per-step visual targeting surface.
// ============================================================
// Why a dedicated component?
// The spotlight controls are richer than a single dropdown: target mode
// (preset vs manual selector), grouped vs single matching, plus optional
// fine-tune knobs (padding / width / X / Y). Pulling this out keeps the
// main editor body calm and editorial, and lets us layer a soft "prefer
// grouped areas" nudge when admins choose a single-button preset.
const SpotlightEditor = ({ step, presets, onChange }) => {
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const targetMode = step.target_mode || 'preset';
  const currentPreset = presets.find((p) => p.value === (step.target || 'none'));
  const isSingleKindPreset = targetMode === 'preset' && currentPreset && currentPreset.kind === 'single';
  const matchMode = (step.match_mode || '').trim();

  const sectionStyle = {
    padding: '16px 18px',
    border: '1px solid var(--kahla-border)',
    background: '#faf8f5',
  };
  const sectionLabel = {
    fontFamily: 'Playfair Display, serif',
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: 'var(--kahla-text-light)',
    marginBottom: 14,
    fontWeight: 500,
  };

  return (
    <div data-testid={`spotlight-editor-${step.id}`} style={sectionStyle}>
      <div style={sectionLabel}>Spotlight targeting</div>

      {/* Target mode — calm segmented control */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ ...labelStyle, marginBottom: 8 }}>How is the area chosen?</div>
        <div
          role="radiogroup"
          aria-label="Spotlight target mode"
          style={{ display: 'inline-flex', border: '1px solid var(--kahla-border)', background: '#fff' }}
        >
          {TARGET_MODES.map((m) => {
            const selected = targetMode === m.value;
            return (
              <button
                key={m.value}
                type="button"
                role="radio"
                aria-checked={selected}
                data-testid={`spotlight-mode-${m.value}-${step.id}`}
                onClick={() => onChange({ target_mode: m.value })}
                style={{
                  minWidth: 150, padding: '8px 16px',
                  fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  fontWeight: selected ? 600 : 500,
                  background: selected ? 'var(--kahla-charcoal)' : 'transparent',
                  color: selected ? '#fff' : 'var(--kahla-text-light)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.18s ease, color 0.18s ease',
                }}
              >{m.label}</button>
            );
          })}
        </div>
      </div>

      {/* Preset OR custom selector — conditional, never both. */}
      {targetMode === 'preset' ? (
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Spotlight target</label>
          <select
            value={step.target || 'none'}
            onChange={(e) => onChange({ target: e.target.value })}
            data-testid={`step-target-${step.id}`}
            style={inputStyle}
          >
            {presets.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}{p.kind === 'group' ? '  · grouped area' : ''}
              </option>
            ))}
          </select>
          {isSingleKindPreset && (
            <p
              data-testid={`spotlight-single-hint-${step.id}`}
              style={{
                marginTop: 8,
                fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                fontStyle: 'italic',
                fontSize: 13,
                lineHeight: 1.55,
                color: '#7a6b56',
              }}
            >
              This highlights a single small element. For a calmer, more
              intentional moment, consider a grouped area (e.g. the full
              exports row instead of just one button).
            </p>
          )}
        </div>
      ) : (
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Manual CSS selector</label>
          <input
            type="text"
            value={step.custom_selector || ''}
            onChange={(e) => onChange({ custom_selector: e.target.value })}
            data-testid={`step-custom-selector-${step.id}`}
            placeholder='e.g. [data-testid="add-images-section"]'
            style={{ ...inputStyle, fontFamily: 'Menlo, Consolas, monospace', fontSize: 12 }}
          />
          <p style={{
            marginTop: 6,
            fontSize: 11, lineHeight: 1.55,
            color: 'var(--kahla-text-light)',
          }}>
            Any standard CSS selector — IDs, classes, attributes. Use a comma to
            target multiple elements and set match mode to <em>Whole group</em>.
          </p>
        </div>
      )}

      {/* Match mode — only meaningful when the selector may match multiple elements. */}
      <div style={{ marginBottom: 4 }}>
        <label style={labelStyle}>If multiple elements match</label>
        <select
          value={matchMode}
          onChange={(e) => onChange({ match_mode: e.target.value })}
          data-testid={`step-match-mode-${step.id}`}
          style={inputStyle}
        >
          {MATCH_MODES.map((m) => (
            <option key={m.value || 'auto'} value={m.value}>{m.label}</option>
          ))}
        </select>
      </div>

      {/* Pre-open selector — used when the spotlight target lives inside a
          collapsible panel (e.g. PDF / ZIP exports inside the Settings drawer).
          The tour clicks this selector before measuring the rect, so the
          target is in the DOM when we draw the ring. Presets that need this
          already ship with a sensible default; admins can override here. */}
      <div style={{ marginBottom: 8, marginTop: 12 }}>
        <label style={labelStyle}>Open before highlighting <span style={{ fontStyle: 'italic', color: 'var(--kahla-text-light)', textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
        <input
          type="text"
          value={step.pre_open_selector ?? ((currentPreset && currentPreset.pre_open) || '')}
          onChange={(e) => onChange({ pre_open_selector: e.target.value })}
          placeholder='e.g. [data-testid="board-settings-btn"]'
          data-testid={`step-pre-open-${step.id}`}
          style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }}
        />
        <p style={{
          fontSize: 11,
          color: 'var(--kahla-text-light)',
          fontStyle: 'italic',
          margin: '6px 0 0',
          lineHeight: 1.5,
        }}>
          {currentPreset?.pre_open
            ? `This preset opens ${currentPreset.pre_open} before highlighting. Clear to disable, or paste another selector to override.`
            : 'CSS selector for an element to click before measuring (useful when the target lives inside a drawer or collapsed panel).'}
        </p>
      </div>

      {/* Fine-tune — collapsed by default, opens to numeric overrides. */}
      <div style={{ marginTop: 14, borderTop: '1px solid var(--kahla-border)', paddingTop: 12 }}>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          data-testid={`spotlight-finetune-toggle-${step.id}`}
          style={{
            background: 'none', border: 'none', padding: 0,
            fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: 'var(--kahla-text-light)',
            cursor: 'pointer',
          }}
        >
          {showAdvanced ? '–' : '+'} Fine-tune spotlight
        </button>

        {showAdvanced && (
          <div
            data-testid={`spotlight-finetune-${step.id}`}
            style={{
              marginTop: 12,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              gap: 12,
            }}
          >
            <FineTuneField
              label="Padding"
              suffix="px"
              value={step.spotlight_padding ?? 14}
              onChange={(v) => onChange({ spotlight_padding: v })}
              testId={`spotlight-padding-${step.id}`}
              min={0} max={80}
            />
            <FineTuneField
              label="Width override"
              suffix="px"
              placeholder="auto"
              value={step.spotlight_width_override ?? ''}
              onChange={(v) => onChange({ spotlight_width_override: v === '' ? null : v })}
              testId={`spotlight-width-${step.id}`}
              min={40} max={3000}
              allowEmpty
            />
            <FineTuneField
              label="Offset X"
              suffix="px"
              value={step.spotlight_offset_x ?? 0}
              onChange={(v) => onChange({ spotlight_offset_x: v })}
              testId={`spotlight-offset-x-${step.id}`}
              min={-1000} max={1000}
            />
            <FineTuneField
              label="Offset Y"
              suffix="px"
              value={step.spotlight_offset_y ?? 0}
              onChange={(v) => onChange({ spotlight_offset_y: v })}
              testId={`spotlight-offset-y-${step.id}`}
              min={-1000} max={1000}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const FineTuneField = ({ label, suffix, value, onChange, testId, min, max, placeholder, allowEmpty }) => (
  <div>
    <div style={{ fontSize: 10, letterSpacing: '0.04em', color: 'var(--kahla-text-light)', marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        type="number"
        value={value === null || value === undefined ? '' : value}
        placeholder={placeholder || ''}
        min={min}
        max={max}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '' && allowEmpty) { onChange(''); return; }
          const n = parseInt(raw, 10);
          if (Number.isNaN(n)) return;
          onChange(n);
        }}
        data-testid={testId}
        style={{ ...inputStyle, padding: '8px 10px', fontSize: 13 }}
      />
      <span style={{ fontSize: 11, color: 'var(--kahla-text-light)' }}>{suffix}</span>
    </div>
  </div>
);

export default AdminOnboardingFlow;
